<template>
  <div>
    <base-toolbar :title="category_name"></base-toolbar>

    <v-container fluid class="pt-0">
      <v-row justify="center" class="d-none d-sm-flex border-b">
        <v-col cols="12" md="8">
          <v-row align="center">
            <v-col class="py-2 page-title" v-text="category_name"> </v-col>
            <v-col cols="auto" class="font-weight-medium text-body-2 pr-0">
              {{ $t("SORT_BY") }}
            </v-col>
            <v-col cols="3" xl="2">
              <v-select
                v-model="sort_by"
                :items="sort_by_option"
                :append-icon="mdiChevronDown"
                dense
                outlined
                label="Sort By"
                hide-details
                single-line
                @change="sortList"
              ></v-select>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <v-row justify="center" class="border-b">
        <v-col class="d-none d-sm-block pt-0">
          <v-row align="center" class="border-b">
            <v-col class="font-weight-medium">
              {{ $t("FILTER") }}
            </v-col>
            <v-col cols="auto">
              <v-btn small text @click="applyFilter([])">
                {{ $t("CLEAR_ALL") }}
              </v-btn>
            </v-col>
          </v-row>

          <v-list color="transparent">
            <v-list-group>
              <template v-slot:activator>
                <v-list-item-content>
                  <v-list-item-title
                    v-text="$t('BY_RELIGIONS')"
                  ></v-list-item-title>
                </v-list-item-content>
              </template>

              <v-list-item-group
                v-model="template_type_id_list"
                multiple
                @change="onFilter"
              >
                <v-list-item
                  v-for="template_type in template_type_list"
                  :key="template_type.id"
                  :value="template_type.id.toString()"
                >
                  <template v-slot:default="{ active }">
                    <v-list-item-content>
                      <v-list-item-title
                        v-text="template_type.localized_name"
                      ></v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-checkbox :input-value="active"></v-checkbox>
                    </v-list-item-action>
                  </template>
                </v-list-item>
              </v-list-item-group>
            </v-list-group>
          </v-list>
        </v-col>

        <v-col cols="12" md="8" class="border-x px-6">
          <v-row>
            <v-col
              v-for="(template, j) in template_list"
              :key="j"
              cols="6"
              sm="3"
              xl="2"
            >
              <thumbnail-preview :template="template"></thumbnail-preview>
            </v-col>

            <!-- Sentinel element for detecting that end of list is reached -->
            <v-col
              v-if="!this.$store.state.page_loader && template_list.length"
              v-intersect="onSentinelIntersect"
            ></v-col>
          </v-row>
        </v-col>

        <v-col class="d-none d-sm-block"> </v-col>
      </v-row>

      <MobileFilterDialog
        :template_type_list="template_type_list"
        :template_type_id_list="template_type_id_list"
        :sort_by_option="sort_by_option"
        :sort_by="sort_by"
        @filter="applyFilter"
        @sort="sortList"
      ></MobileFilterDialog>
    </v-container>
  </div>
</template>

<script>
import keys from "@/app-js/keys.js";
import urls from "@/app-js/urls.js";
import { mdiChevronDown } from "@mdi/js";
import ThumbnailPreview from "@/components/ThumbnailPreview";

export default {
  metaInfo() {
    return {
      title: this.category_name,
      meta: [
        {
          vmid: "description",
          name: "description",
          content: `Create ${this.category_name} online easily with WedNicely's invitation card maker.`
        }
      ]
    };
  },

  components: {
    ThumbnailPreview,
    MobileFilterDialog: () => import("@/components/dialogs/MobileFilterDialog"),
    BaseToolbar: () => import("@/components/BaseToolbar")
  },

  data() {
    return {
      mdiChevronDown,
      category_name: "",
      category_id: "",
      template_list: [],
      template_type_list: [],
      template_type_id_list: [],
      by_photo_filter_option: ["Both", "With Photo", "Without Photo"],
      by_photo_filter: [],
      sort_by: this.$store.state.sort_list.split(",")[0],
      sort_by_option: this.$store.state.sort_list.split(","),
      panel: [0],
      page_number: 1,
      total_pages: 0,
      isSentinelIntersecting: false
    };
  },

  watch: {
    $route: "fetchData",

    isSentinelIntersecting: {
      async handler(newVal) {
        if (!newVal || this.page_number === this.total_pages) return;
        this.page_number++;
        await this.fetchTemplateList();
      }
    }
  },

  async prefetch() {
    await this.fetchData();
  },

  methods: {
    async fetchData() {
      this.template_list = [];
      this.page_number = 1;
      this.total_pages = 0;
      await this.fetchFilterCategory();
      await this.fetchTemplateList();
    },

    async fetchTemplateList() {
      this.category_id = this.$route.params.category_id;
      const url = urls.TEMPLATES;
      const params = {
        category_id: this.category_id,
        lang: this.$store.state.language,
        sort_type: this.sort_by,
        page_number: this.page_number.toString(),
        template_type_id_list: JSON.stringify(this.template_type_id_list)
      };
      const response = await this.$axios.get(url, { params });
      this.total_pages = response.data.total_pages;
      this.category_name = response.data.category.category_name;
      this.template_list = this.template_list.concat(response.data.templates);

      const items = this.template_list.map(template => ({
        item_id: template.id,
        item_category: this.category_name,
        price: parseFloat(template.price_android_discounted),
        currency: "INR"
      }));

      this.logFirebaseEvent("VIEW_ITEM_LIST", {
        item_list_id: this.category_id,
        item_list_name: this.category_name,
        items
      });
    },

    async fetchFilterCategory() {
      let url = urls.CARD_WEDDING_CARD_CATEGORY;
      let params = {
        lang: this.$store.state.language,
        parent_category_type: keys.PDF_CARD,
        card_type: keys.INVITATION
      };
      const response = await this.$axios.get(url, { params });
      this.template_type_list = response.data.template_type_list;
    },

    sortList(sort_by = this.sort_by) {
      this.sort_by = sort_by;
      this.page_number = 1;
      this.fetchTemplateList();
    },

    async applyFilter(template_type_id_list) {
      this.template_type_id_list = template_type_id_list;
      await this.onFilter();
    },

    onSentinelIntersect(entries, observer) {
      if (this.page_number == this.total_pages) {
        observer.disconnect();
        return;
      }
      this.isSentinelIntersecting = entries[0].isIntersecting;
    },

    async onFilter() {
      this.page_number = 1;
      this.template_list = [];
      await this.fetchTemplateList();
    }
  }
};
</script>
